import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Users, DollarSign, Search } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const TypesPersonnelPaie = () => {
  const navigate = useNavigate();
  const { activiteId } = useParams();
  const { filialeId, isConsolidated } = useFiliale();
  const [typesPersonnel, setTypesPersonnel] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [activite, setActivite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [activiteId, filialeId, isConsolidated]);

  useEffect(() => {
    // Filtrer par terme de recherche
    const filtered = typesPersonnel.filter(type =>
      type.libelle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTypes(filtered);
    setCurrentPage(1); // Réinitialiser à la première page lors de la recherche
  }, [searchTerm, typesPersonnel]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTypes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Récupérer l'activité
      const activiteRes = await http.get(`/api/activites/${activiteId}`);
      setActivite(activiteRes.data);

      // Récupérer les types de personnel filtrés par activité et filiale
      const params = { idactivite: activiteId };
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      const typesRes = await http.get('/api/types-personnel', { params });
      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];

      setTypesPersonnel(allTypes);
    } catch (error) {
      console.error('Erreur récupération données:', error);
      setTypesPersonnel([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Types de Personnel"
        subtitle={`Activité : ${activite?.Libelle_activite || 'Chargement...'}`}
        showBack={true}
        onBack={() => navigate('/elements-paie')}
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Types de Personnel</h3>
                <p className="text-sm text-gray-500 mt-1">Cliquez sur un type de personnel pour accéder au calcul de paie</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {filteredTypes.length} type(s) de personnel trouvé(s)
            </p>
          </div>

          {filteredTypes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {searchTerm ? 'Aucun résultat trouvé pour cette recherche' : 'Aucun type de personnel trouvé pour cette activité'}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {currentItems.map((type) => (
                  <div
                    key={type.idtype_personnel}
                    onClick={() => navigate(`/calcul-paie/${type.idtype_personnel}`)}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{type.libelle}</h4>
                        <p className="text-sm text-gray-500">ID: {type.idtype_personnel} {type.code && `• ${type.code}`}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Page {currentPage} sur {totalPages} ({filteredTypes.length} éléments)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TypesPersonnelPaie;
